import SegmentLoadingButton from 'ui-component/Segment/SegmentLoadingButton';
import { GridLegacy } from '@mui/material';
import { useSelector } from 'store';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { FieldValues, useForm } from 'react-hook-form';

import { useGetOrgQuery, useUpdateOrgMutation } from 'store/slices/apiV1/org';
import { HookFormToggleCardButton } from 'ui-component/HookFormComponents';
import SegmentButton from 'ui-component/Segment/SegmentButton';

import { useEffect, useState } from 'react';
import { handleErr } from 'utils/functions';
import CardSectionWithAction from 'ui-component/cards/CardSectionWithAction';
import { ALL_APPS, ALL_FEATURE_IDS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import { gridSpacing } from 'constants/themeConstants';
import { useAppAccessContext } from 'contexts/AppAccessContext';
import useSnackbar from 'hooks/useSnackbar';

const PartPreferences = () => {
  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();

  const { activeOrgId } = useSelector((state) => state.org);
  const { isPermissionDisabled } = useAppAccessContext();
  const customPnsEnabled = !isPermissionDisabled({
    app: ALL_APPS.PART_LIBRARY.id,
    feature: ALL_FEATURE_IDS.CUSTOM_PNS,
  });

  const { data: org, isLoading: isLoadingOrg } = useGetOrgQuery(
    activeOrgId ?? skipToken
  );
  const [updateOrg, { isLoading: isUpdatingOrg }] = useUpdateOrgMutation();
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = useForm<FieldValues>({
    defaultValues: {
      timezone: org?.timezone,
      bomMissingCustomIdWarning: org?.bomMissingCustomIdWarning,
      bomUploadCustomId: org?.bomUploadCustomId,
      onlyOrgPartLibrary: org?.onlyOrgPartLibrary,
    },
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (org) {
      reset();
    }
  }, [isLoadingOrg]);

  const onSubmit = async (payload: FieldValues) => {
    if (isDirty) {
      try {
        await updateOrg({
          orgId: activeOrgId!,
          payload,
        }).unwrap();
        dispatchSuccessSnackbar('Preferences updated');
        setIsEditing(false);
      } catch (err) {
        handleErr(err, (errMessage: string) => {
          dispatchErrorSnackbar(errMessage);
        });
      }
    } else {
      setIsEditing(false);
    }
  };

  const actionButton = isEditing ? (
    <SegmentLoadingButton
      type="submit"
      variant="contained"
      color="primary"
      size="medium"
      loading={isUpdatingOrg}
      permissionScope={{
        app: ALL_APPS.ORG_SETTINGS.id,
        accessLevel: appPermissionAccessLevels.edit,
      }}
    >
      {isDirty ? 'Save' : 'Cancel'}
    </SegmentLoadingButton>
  ) : (
    <SegmentButton
      type="button"
      variant="contained"
      onClick={() => setIsEditing(true)}
      sx={{ float: 'right' }}
      permissionScope={{
        app: ALL_APPS.ORG_SETTINGS.id,
        accessLevel: appPermissionAccessLevels.edit,
      }}
    >
      Edit
    </SegmentButton>
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <GridLegacy container spacing={gridSpacing}>
        <GridLegacy item xs={12}>
          <CardSectionWithAction
            contents={
              <GridLegacy container spacing={gridSpacing}>
                <GridLegacy item xs={12}>
                  <HookFormToggleCardButton
                    name="onlyOrgPartLibrary"
                    control={control}
                    errors={errors}
                    disabled={!isEditing}
                    label={`Automatically Create Parts in ${org?.name}'s Library`}
                    options={[
                      {
                        value: false,
                        label: 'Enabled',
                        description: `Parts will automatically be created and updated in ${org?.name}'s Library via BOM Upload, DocAI, and Punchout. Use this setting if you want your library to dynamically update based on user activity in Cofactr.`,
                      },
                      {
                        value: true,
                        label: 'Disabled',
                        description: `Parts will not automatically be created or updated in ${org?.name}'s Library via BOM Upload, DocAI, and Punchout. Parts will have to be explicitly added to the library manually or via integrations. Use this setting if you want strict control of your library.`,
                      },
                    ]}
                  />
                </GridLegacy>
              </GridLegacy>
            }
            action={actionButton}
            title="Part Library"
          />
        </GridLegacy>
        {customPnsEnabled && (
          <GridLegacy item xs={12}>
            <CardSectionWithAction
              contents={
                <GridLegacy container spacing={gridSpacing}>
                  <GridLegacy item xs={12}>
                    <HookFormToggleCardButton
                      name="bomMissingCustomIdWarning"
                      control={control}
                      errors={errors}
                      disabled={!isEditing}
                      label={`Warnings for Parts in a BOM without a ${
                        org?.name || 'Custom'
                      } Part Number`}
                      options={[
                        {
                          value: 'allowed',
                          label: 'None',
                          description: `${
                            org?.name || 'Custom'
                          } Part Numbers are not required, and no warnings will be shown`,
                        },
                        {
                          value: 'warning',
                          label: 'Warning',
                          description: `${
                            org?.name || 'Custom'
                          } Part Numbers are recommended, and a warnings will be shown if they are missing`,
                        },
                        {
                          value: 'error',
                          label: 'Error',
                          description: `${
                            org?.name || 'Custom'
                          } Part Numbers are required, and an error will be shown if they are missing`,
                        },
                      ]}
                    />
                  </GridLegacy>
                  <GridLegacy item xs={12}>
                    <HookFormToggleCardButton
                      name="bomUploadCustomId"
                      control={control}
                      errors={errors}
                      disabled={!isEditing}
                      label={`Setting ${
                        org?.name || 'Custom'
                      } Part Numbers During BOM Uploads`}
                      options={[
                        {
                          value: 'none',
                          label: 'Disabled',
                          description: `${
                            org?.name || 'Custom'
                          } Part Numbers included in BOM uploads will be used to help match parts in your library, but user will not be shown the options to save uploaded ${
                            org?.name || 'Custom'
                          } Part Numbers to part records in the BOM view. This is the recommended setting if ${
                            org?.name || 'Custom'
                          } Part Numbers are uploaded to Cofactr via an integration or otherwise centrally managed.`,
                        },
                        {
                          value: 'different_punctuation',
                          label: 'Enabled',
                          description: `${
                            org?.name || 'Custom'
                          } Part Numbers included in BOM uploads will be used to help match parts in your library. Users will be shown the options to save uploaded ${
                            org?.name || 'Custom'
                          } Part Numbers for all matched parts in the BOM view.`,
                        },
                      ]}
                    />
                  </GridLegacy>
                </GridLegacy>
              }
              action={actionButton}
              title={`${org?.name || 'Custom'} Part Numbers`}
            />
          </GridLegacy>
        )}
      </GridLegacy>
    </form>
  );
};

export default PartPreferences;
